/* eslint-disable max-lines */
import { getCurrentQuizType, getQuizDetails } from '@selectors/quizTaking';
import { HOST_TYPE } from '@utils/api-helper/host';
import paginateAction from '@utils/paginateAction';
import { paginatorListSchema } from '../schemas';
import quizTakingTypes from '../constants/quizTaking';

export const getQuizEndpointByType = (endpoint, quizType = {}) => {
  const { isSubjective } = quizType;

  if (isSubjective) {
    return endpoint
      .replace(/\/quiz\//, '/sts-quiz/')
      .replace(/\/session\//, '/sts-session/');
  }

  return endpoint;
};

const getHostByType = (quizType) => {
  const { isSubjective } = quizType;

  if (isSubjective) {
    return HOST_TYPE.DEFAULT;
  }

  return HOST_TYPE.TEST_SERIES;
};

export const fetchQuizData = (quizUID) => ({
  type: quizTakingTypes.QUIZ_FETCH_DETAILS,
  endpoint: `v3/quizzes/quiz/${quizUID}/details/`,
  method: 'get',
  quizUID,
  useSearchToken: true,
  hostType: HOST_TYPE.TEST_SERIES
});

export const fetchQuizAttempts = (quizUID) => (dispatch, getState) => {
  const quizType = getCurrentQuizType(getState());
  const hostType = getHostByType(quizType);
  const endpoint = getQuizEndpointByType(
    `v1/quizzes/quiz/${quizUID}/my_attempts/`,
    quizType
  );

  return dispatch({
    type: quizTakingTypes.QUIZ_FETCH_ATTEMPTS,
    endpoint,
    method: 'get',
    quizUID,
    useSearchToken: hostType !== 'DEFAULT',
    hostType
  });
};

export const fetchQuizMinimalData = (quizUID) => async (dispatch, getState) => {
  await dispatch(fetchQuizData(quizUID));

  const { is_whitelisted } = getQuizDetails(quizUID)(getState());

  if (is_whitelisted) {
    await dispatch(fetchQuizAttempts(quizUID));
  }
};

export const fetchStaticQuestions = (quizUID) => (dispatch, getState) => {
  const quizType = getCurrentQuizType(getState());
  const hostType = getHostByType(quizType);
  const endpoint = getQuizEndpointByType(
    `v1/quizzes/quiz/${quizUID}/questions/`,
    quizType
  );

  return dispatch({
    type: quizTakingTypes.QUIZ_FETCH_STATIC_QUESTIONS,
    endpoint,
    method: 'get',
    quizUID,
    useSearchToken: hostType !== 'DEFAULT',
    hostType
  });
};

export const enrollInQuiz =
  (
    quizUID,
    selectedLanguageCode,
    isRevampedTestVersion2,
    proctoringSessionId = null
  ) =>
  (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);

    const { isSubjective } = quizType;
    const version = isSubjective ? 'v2' : 'v3';
    const endpoint = getQuizEndpointByType(
      `${version}/quizzes/quiz/${quizUID}/enroll/`,
      quizType
    );

    // Prepare request data
    const data = {
      language: selectedLanguageCode
    };

    // Add proctoring session ID if provided (only for proctored tests)
    if (proctoringSessionId) {
      data.proctoring_session_id = proctoringSessionId;
    }

    return dispatch({
      type: quizTakingTypes.QUIZ_ENROLL,
      endpoint,
      method: 'post',
      data,
      quizUID,
      useSearchToken: hostType !== 'DEFAULT',
      hostType,
      isRevampedTestVersion2
    }).then((resp) => {
      // For objective fetch the questions
      if (!resp.error && !isSubjective) {
        return dispatch(fetchStaticQuestions(quizUID));
      }

      return resp;
    });
  };

const fetchSubmittedQuestions =
  (quizUID, sessionUID) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);

    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/submissions/`,
      quizType
    );
    const { isSubjective } = quizType;

    return dispatch({
      type: quizTakingTypes.QUIZ_FETCH_SUBMITTED_QUESTIONS,
      endpoint,
      method: 'get',
      quizUID,
      sessionUID,
      isSubjective,
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const fetchQuestionsWithAttempts =
  (quizUID, sessionUID) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);

    return dispatch({
      type: quizTakingTypes.QUIZ_FETCH_QUESTIONS_WITH_ATTEMPTS,
      endpoint: `v2/quizzes/sts-session/${sessionUID}/get_questions/`,
      method: 'get',
      quizUID,
      sessionUID,
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const fetchActiveSessionData =
  (quizUID, sessionUID, isRevampedTestVersion2) =>
  async (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const { isSubjective } = quizType;

    if (isSubjective && !isRevampedTestVersion2) {
      await dispatch(fetchQuestionsWithAttempts(quizUID, sessionUID));
      return;
    }

    await dispatch(fetchStaticQuestions(quizUID));
    await dispatch(fetchSubmittedQuestions(quizUID, sessionUID));
  };

export const toggleMarkQuestion =
  (quizUID, sessionUID, sectionUID, questionUID, isMarked) =>
  (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/mark_unmark_question/`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_MARK_QUESTION,
      endpoint,
      method: 'post',
      sectionUID,
      questionUID,
      isMarked,
      data: {
        q_uid: questionUID,
        is_marked: isMarked ? 1 : 0
      },
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const submitAnswerResponse =
  (quizUID, sectionUID, sessionUID, data) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v2/quizzes/session/${sessionUID}/question_response/`,
      quizType
    );
    const { isSubjective } = quizType;
    return dispatch({
      type: quizTakingTypes.QUIZ_SUBMIT_QUESTION_RESPONSE,
      endpoint,
      method: 'post',
      data,
      sectionUID,
      questionUID: data.question_uid,
      attemptList: data.attempt_list,
      isSubjective,
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const sendViewedQuestion =
  ({ sessionUID, questionUID, sectionUID, isRevampedTestVersion2 }) =>
  (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v2/quizzes/session/${sessionUID}/viewed_question/`,
      quizType
    );

    const questionData = { questionUID, sectionUID, isRevampedTestVersion2 };
    return dispatch({
      type: quizTakingTypes.QUIZ_SEND_VIEWED_QUESTION,
      endpoint,
      method: 'post',
      data: {
        question_uid: questionUID
      },
      useSearchToken: hostType !== 'DEFAULT',
      hostType,
      questionData
    });
  };

export const finishSection =
  (quizUID, sessionUID, sectionUID) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/finish_section/`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_FINISH_SECTION,
      endpoint,
      method: 'post',
      data: { section_uid: sectionUID },
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const finishQuiz =
  (quizUID, sessionUID, isDSTFlow) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = isDSTFlow
      ? `scholarship/v1/${sessionUID}/finish_test`
      : getQuizEndpointByType(
          `v1/quizzes/session/${sessionUID}/finish_quiz/`,
          quizType
        );

    return dispatch({
      type: quizTakingTypes.QUIZ_FINISH_QUIZ,
      endpoint,
      method: 'post',
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const submitBulkAnswers =
  (quizUID, sessionUID, data) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/bulk_submit_question_response/`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_SUBMIT_BULK_ANSWERS,
      endpoint,
      method: 'post',
      data: {
        question_response: data
      },
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const fetchQuestionReportOptions = () => ({
  type: quizTakingTypes.QUIZ_FETCH_QUESTION_REPORT_OPTIONS,
  endpoint: 'v1/feedback/question/',
  method: 'get'
});

export const fetchSolutionFeedbackOptions = () => ({
  type: quizTakingTypes.QUIZ_FETCH_SOLUTION_FEEDBACK_OPTIONS,
  endpoint: 'v1/feedback/question_solution/',
  method: 'get'
});

export const fetchVideoSolutionFeedbackOptions = () => ({
  type: quizTakingTypes.QUIZ_FETCH_VIDEO_SOLUTION_FEEDBACK_OPTIONS,
  endpoint: 'v1/feedback/contest_question_solution/',
  method: 'get'
});

export const submitVideoSolutionFeedback = (UID, data) => ({
  type: quizTakingTypes.QUIZ_SUBMIT_VIDEO_SOLUTION_FEEDBACK,
  endpoint: `v1/feedback/${UID}/response/`,
  method: 'post',
  data
});

export const fetchSessionDetails =
  (quizUID, sessionUID) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/details/`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_FETCH_SESSION_DETAILS,
      endpoint,
      method: 'get',
      sessionUID,
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const fetchSolutionFilters =
  (quizUID, sessionUID) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/get_filter/`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_FETCH_SOLUTION_FILTER,
      endpoint,
      method: 'get',
      sessionUID,
      useSearchToken: true,
      hostType
    });
  };

const getQuizSolutions =
  (sessionUID, resetWithoutEmpty, hostType) => (next) => ({
    type: quizTakingTypes.QUIZ_FETCH_PAGINATED_SOLUTIONS,
    endpoint: next,
    method: 'get',
    key: sessionUID,
    resetWithoutEmpty,
    useSearchToken: hostType !== 'DEFAULT',
    hostType
  });

export const fetchSolutions = (
  quizUID,
  sessionUID,
  quizType = {},
  key,
  data,
  nextPage,
  resetWithoutEmpty = false
) => {
  const hostType = getHostByType(quizType);
  const endpoint = getQuizEndpointByType(
    `v2/quizzes/session/${sessionUID}/get_questions_paginated_with_attempts/?attempt_type=${
      data.solutionType
    }&${data.hasDefaultSection ? 'topic_group' : 'section_uid'}=${
      data.sectionUID
    }&topic_uid=${data.topicUID}&limit=15`,
    quizType
  );

  return paginateAction(
    endpoint,
    getQuizSolutions(key, resetWithoutEmpty, hostType),
    'quizSolutions',
    key,
    resetWithoutEmpty
  )(nextPage);
};

export const fetchQuizCommonReport = (quizUID, sessionUID) => (dispatch) => {
  const endpoint = `v1/quizzes/quiz/${quizUID}/common_report/`;

  return dispatch({
    type: quizTakingTypes.QUIZ_FETCH_COMMON_REPORT,
    endpoint,
    method: 'get',
    sessionUID,
    useSearchToken: true,
    hostType: HOST_TYPE.TEST_SERIES
  });
};

export const fetchDSTQuizCommonReport = (quizUID, sessionUID) => (dispatch) => {
  const endpoint = `scholarship/v1/${sessionUID}/result`;

  return dispatch({
    type: quizTakingTypes.QUIZ_FETCH_COMMON_REPORT,
    endpoint,
    method: 'get',
    sessionUID,
    useSearchToken: true,
    hostType: HOST_TYPE.TEST_SERIES
  });
};

export const fetchQuizAnalytics =
  (quizUID, sessionUID, isDST) => async (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const { is_whitelisted } = getQuizDetails(quizUID)(getState());

    const endpoint = getQuizEndpointByType(
      `${
        is_whitelisted ? 'v2' : 'v1'
      }/quizzes/session/${sessionUID}/get_elastic_analytics_report/`,
      quizType
    );

    if (isDST) {
      await dispatch(fetchDSTQuizCommonReport(quizUID, sessionUID));
    } else if (is_whitelisted) {
      await dispatch(fetchQuizCommonReport(quizUID, sessionUID));
    }

    return dispatch({
      type: quizTakingTypes.QUIZ_FETCH_ANALYTICS_REPORT,
      endpoint,
      method: 'get',
      sessionUID,
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

export const fetchQuizRank =
  (quizUID, sessionUID, isLive) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const { is_whitelisted } = getQuizDetails(quizUID)(getState());

    const endpoint = getQuizEndpointByType(
      `${
        is_whitelisted ? 'v2' : 'v1'
      }/quizzes/quiz/${quizUID}/get_user_rank/?is_live=${isLive ? 1 : 0}`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_FETCH_USER_RANK,
      isWhitelisted: is_whitelisted,
      endpoint,
      method: 'get',
      quizUID,
      sessionUID,
      useSearchToken: true,
      hostType
    });
  };

export const setQuizLoadingState = (value) => (dispatch) => {
  return dispatch({
    type: quizTakingTypes.QUIZ_LOADING,
    value
  });
};

export const fetchEvaluatedSheet =
  (quizUID, sessionUID) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/get_evaluated_sheet/`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_FETCH_EVALUATED_SHEET,
      endpoint,
      method: 'get',
      sessionUID,
      useSearchToken: true,
      hostType
    });
  };

export const fetchQuizFeedbackOptions = (quizUID) => ({
  type: quizTakingTypes.QUIZ_FETCH_FEEDBACK_OPTIONS,
  endpoint: `v1/feedback/test/?object_uid=${quizUID}`,
  method: 'get',
  quizUID
});

export const rejectSubjectiveAnswerUpload =
  (quizUID, sessionUID) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/reject_upload/`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_REJECT_SUBJECTIVE_ANSWER_UPLOAD,
      endpoint,
      method: 'post',
      useSearchToken: true,
      hostType
    });
  };

export const uploadSubjectiveAnswer =
  (quizUID, sessionUID, urls) => (dispatch, getState) => {
    const quizType = getCurrentQuizType(getState());
    const hostType = getHostByType(quizType);
    const endpoint = getQuizEndpointByType(
      `v1/quizzes/session/${sessionUID}/upload_answers/`,
      quizType
    );

    return dispatch({
      type: quizTakingTypes.QUIZ_UPLOAD_SUBJECTIVE_ANSWER,
      endpoint,
      method: 'post',
      data: {
        s3url_list: urls
      },
      useSearchToken: hostType !== 'DEFAULT',
      hostType
    });
  };

const getQuizLeaderboard =
  (key, isLive, resetWithoutEmpty, hostType) => (next) => ({
    type: isLive
      ? quizTakingTypes.QUIZ_FETCH_LEADERBOARD_LIVE
      : quizTakingTypes.QUIZ_FETCH_LEADERBOARD_ALL,
    endpoint: next,
    method: 'get',
    key,
    resetWithoutEmpty,
    schema: paginatorListSchema,
    useSearchToken: true,
    hostType
  });

export const fetchQuizLeaderBoard = ({
  quizUID,
  isLive,
  nextPage,
  resetWithoutEmpty = false,
  quizType = {}
}) => {
  const key = isLive ? 'quizLeaderboardLive' : 'quizLeaderboardAll';
  const hostType = getHostByType(quizType);
  const endpoint = getQuizEndpointByType(
    `v2/quizzes/quiz/${quizUID}/get_quiz_leaderboard/?is_live=${
      isLive ? 1 : 0
    }&limit=15&offset=0`,
    quizType
  );

  return paginateAction(
    endpoint,
    getQuizLeaderboard(quizUID, isLive, resetWithoutEmpty, hostType),
    key,
    quizUID,
    resetWithoutEmpty
  )(nextPage);
};

export const cleanUpAfterQuizEnds = () => ({
  type: quizTakingTypes.QUIZ_CLEAN_UP_AFTER_QUIZ_END
});

export const setCurrentQuestionInfo = (data) => ({
  type: quizTakingTypes.QUIZ_SET_CURRENT_QUESTION_TYPE,
  data
});

export const setCurrentSelectedAnswer = (data) => ({
  type: quizTakingTypes.QUIZ_SET_CURRENT_SELECTED_ANSWER,
  data
});

export const setModalState = (data) => ({
  type: quizTakingTypes.QUIZ_MODAL_TYPE,
  data
});

export const setLanguage = (data) => ({
  type: quizTakingTypes.QUIZ_SET_SELECTED_LANGUAGE,
  data
});
